import './SettingsPicker.scss'
import { useTranslation } from 'react-i18next'
import React, { useEffect, useState } from 'react'
import InputContainer from '../../../Inputs/Container'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import VectorIcon from './VectorIcon'

export const SettingsDropdown = ({
  settingsItem = '',
  handleSettingChange,
  setIsOpen,
  label,
  ...props
}) => {
  const { t } = useTranslation()

  // Rename the state variable to settingsItemValue to avoid conflict
  const [settingsItemValue, setSettingsItem] = useState(settingsItem)

  const handleSettingsItemChange = ({ target }) => {
    setIsOpen(true)
    setSettingsItem(target.value)
  }

  const handleInputClick = () => {
    setIsOpen(true)
  }

  useEffect(() => {
    setSettingsItem(settingsItem)
  }, [settingsItem])

  // Add this function to handle the selection
  const handleSelect = selectedSetting => {
    setSettingsItem(selectedSetting)
    setIsOpen(false)

    // Call the original onSelect from parent if it exists
    if (handleSettingChange) {
      handleSettingChange(selectedSetting)
    }
  }

  return (
    <InputContainer label={label}>
      <SettingPicker
        {...props}
        value={settingsItemValue}
        handleSettingChange={handleSettingsItemChange}
        handleInputClick={handleInputClick}
        handleSelect={handleSelect}
        handleCloseSettingsPicker={() => setIsOpen(false)}
      />
    </InputContainer>
  )
}

function searchSettings(allSettingsItems, searchInput) {
  const searchResult = []

  if (!searchInput) {
    // Return all settings when searchInput is empty (for click without typing)
    return allSettingsItems
  }

  const exactMatch = allSettingsItems.find(
    item => item.toLowerCase() === searchInput.toLowerCase()
  )

  if (exactMatch) {
    searchResult.push(exactMatch)
  } else {
    for (let i = 0; i < allSettingsItems.length; i++) {
      if (allSettingsItems[i].toLowerCase().indexOf(searchInput) !== -1) {
        if (searchResult.push(allSettingsItems[i]) >= 5) {
          break
        }
      }
    }
  }

  return searchResult
}

const SettingItem = ({ className, setting, isSelected, ...props }) => {
  return (
    <button
      {...props}
      type="button"
      className={clsx('SettingItem', className, { isSelected })}>
      <span className="SettingItemBody">{setting}</span>
    </button>
  )
}

const SettingList = ({
  handleSelect,
  search,
  selectedIndex,
  setDropdownItems,
  availableSearchResults
}) => {
  const { t } = useTranslation()

  const dropdownRef = React.createRef()

  const searchResult = searchSettings(availableSearchResults, search)

  useEffect(() => {
    setDropdownItems(searchResult)
  }, [search]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = setting => () => handleSelect(setting)

  return (
    <div className="SettingList" ref={dropdownRef}>
      {searchResult.length > 0 ? (
        searchResult.map((setting, i) => (
          <SettingItem
            isSelected={selectedIndex === i}
            key={i}
            setting={setting}
            onClick={handleClick(setting)}
          />
        ))
      ) : (
        <div className="SettingEmpty">{t('no_results')}</div>
      )}
    </div>
  )
}

const SettingPicker = ({
  value,
  allSettings,
  isOpen,
  handleSettingChange,
  handleInputClick,
  handleSelect,
  selectedIndex,
  setDropdownItems,
  handleCloseSettingsPicker
}) => {
  const { t } = useTranslation()
  const pickerRef = React.useRef(null)

  useEffect(() => {
    const handleClickOutside = event => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        if (handleCloseSettingsPicker) {
          handleCloseSettingsPicker()
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handleCloseSettingsPicker])

  const handleInputClickInternal = () => {
    if (handleInputClick) {
      handleInputClick()
    }
    if (!isOpen) {
      setDropdownItems(allSettings)
    }
  }

  return (
    <div className="Setting" ref={pickerRef}>
      <input
        type="text"
        value={value}
        onChange={handleSettingChange}
        onClick={handleInputClickInternal}
        placeholder={t('type_to_search')}
        onFocus={event => event.target.select()}
        className={clsx('SettingInput', {
          isOpen: isOpen
        })}
      />
      <label className="SettingPickerIconLabel" htmlFor="SettingInput">
        <VectorIcon />
      </label>
      {isOpen && (
        <SettingList
          search={value.toLowerCase()}
          handleSelect={handleSelect}
          selectedIndex={selectedIndex}
          setDropdownItems={setDropdownItems}
          availableSearchResults={allSettings}
        />
      )}
    </div>
  )
}

// export default SettingPicker

SettingPicker.propTypes = {
  setting: PropTypes.string,
  isOpen: PropTypes.bool,
  handleSettingChange: PropTypes.func.isRequired,
  handleSelect: PropTypes.func.isRequired
}
